import { fetchAdminSettings, patchAdminSettings } from '@/api/admin';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { formatApiError } from '@/lib/format-api-error';
import {
	formatPushBalancePackageAllowlistText,
	parsePushBalancePackageAllowlistText,
	PUSH_BALANCE_TG_MIN_UZS_DEFAULT,
} from '@crave/config/push-balance-settings';
import axios from 'axios';
import { Banknote } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import SettingsWidgetCard from './settings-widget-card';

const UZ_BANK_PACKAGE_PRESET = [
	'uz.kapitalbank',
	'uz.ipakyulibank.mobile',
	'com.ipakyulibank.mobile',
	'uz.tbcbank',
	'uz.anorbank.mobile',
	'uz.payme',
	'com.click.uz',
	'uz.uzcard.uzcard',
	'uz.humo.humo',
	'com.uzpsb.mobile',
	'uz.asakabank.mobile',
	'uz.agrobank.mobile',
	'uz.trastbank.mobile',
	'uz.infinbank.mobile',
	'uz.uzum.bank',
];

function formatPatchError(error: unknown, fallback: string): string {
	if (axios.isAxiosError(error)) {
		return formatApiError(error.response?.data) ?? fallback;
	}
	return fallback;
}

function getAllowlistFromForm(text: string): string[] {
	return parsePushBalancePackageAllowlistText(text);
}

export default function BalanceCardsSettingsWidget() {
	const [pushBalanceEnabled, setPushBalanceEnabled] = useState(false);
	const [allowlistText, setAllowlistText] = useState('');
	const [pushBalanceTgMinUzs, setPushBalanceTgMinUzs] = useState(PUSH_BALANCE_TG_MIN_UZS_DEFAULT);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		let cancelled = false;

		async function load() {
			try {
				const settings = await fetchAdminSettings();
				if (!cancelled) {
					setPushBalanceEnabled(settings.pushBalanceEnabled);
					setAllowlistText(formatPushBalancePackageAllowlistText(settings.pushBalancePackageAllowlist ?? []));
					setPushBalanceTgMinUzs(settings.pushBalanceTgMinUzs ?? PUSH_BALANCE_TG_MIN_UZS_DEFAULT);
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		void load();

		return () => {
			cancelled = true;
		};
	}, []);

	async function handleToggle(checked: boolean) {
		const previous = pushBalanceEnabled;
		let packageAllowlist: string[] | undefined;

		if (checked) {
			packageAllowlist = getAllowlistFromForm(allowlistText);
			if (packageAllowlist.length === 0) {
				toast.error('Добавьте хотя бы один package name');
				return;
			}
		}

		setPushBalanceEnabled(checked);
		setIsSaving(true);

		try {
			const patch = checked
				? { pushBalanceEnabled: true as const, pushBalancePackageAllowlist: packageAllowlist! }
				: { pushBalanceEnabled: false as const };
			const settings = await patchAdminSettings(patch, { suppressToast: true });
			setPushBalanceEnabled(settings.pushBalanceEnabled);
			setAllowlistText(formatPushBalancePackageAllowlistText(settings.pushBalancePackageAllowlist ?? []));
			setPushBalanceTgMinUzs(settings.pushBalanceTgMinUzs ?? PUSH_BALANCE_TG_MIN_UZS_DEFAULT);
			toast.success(checked ? 'Push-баланс включён' : 'Push-баланс отключён');
		} catch (error) {
			setPushBalanceEnabled(previous);
			toast.error(formatPatchError(error, 'Не удалось изменить настройку'));
		} finally {
			setIsSaving(false);
		}
	}

	function handleAddUzPreset() {
		const merged = parsePushBalancePackageAllowlistText(
			[allowlistText, ...UZ_BANK_PACKAGE_PRESET].filter(Boolean).join('\n'),
		);
		setAllowlistText(formatPushBalancePackageAllowlistText(merged));
		toast.success('Пресет UZ банков добавлен в список');
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();

		const packageAllowlist = getAllowlistFromForm(allowlistText);
		if (pushBalanceEnabled && packageAllowlist.length === 0) {
			toast.error('Добавьте хотя бы один package name');
			return;
		}
		if (!Number.isInteger(pushBalanceTgMinUzs) || pushBalanceTgMinUzs < 1) {
			toast.error('Укажите корректный порог TG');
			return;
		}

		const previousAllowlist = allowlistText;
		const previousTgMin = pushBalanceTgMinUzs;
		setIsSaving(true);

		try {
			const settings = await patchAdminSettings(
				{
					pushBalancePackageAllowlist: packageAllowlist,
					pushBalanceTgMinUzs,
				},
				{ suppressToast: true },
			);
			setAllowlistText(formatPushBalancePackageAllowlistText(settings.pushBalancePackageAllowlist ?? []));
			setPushBalanceTgMinUzs(settings.pushBalanceTgMinUzs ?? PUSH_BALANCE_TG_MIN_UZS_DEFAULT);
			toast.success('Настройки балансов сохранены');
		} catch (error) {
			setAllowlistText(previousAllowlist);
			setPushBalanceTgMinUzs(previousTgMin);
			toast.error(formatPatchError(error, 'Не удалось сохранить настройки'));
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<SettingsWidgetCard
			id='balance-cards'
			icon={Banknote}
			title='Балансы и карты'
			description='Push-уведомления банковских приложений → баланс и карты'
			titleClassName='text-emerald-600 dark:text-emerald-400'
			iconWrapperClassName='bg-emerald-500/15'
		>
			<LoadingWrapper show={isSaving}>
				<CardContent className='px-4 py-4 flex flex-col gap-4'>
					<div className='flex items-center justify-between gap-3'>
						<div className='flex flex-col gap-0.5'>
							<Label>Push → баланс</Label>
							<span className='text-xs text-muted-foreground'>
								Только push от приложений из списка создают balance/card sync и TG-алерт
							</span>
						</div>
						<Switch
							checked={pushBalanceEnabled}
							onCheckedChange={checked => void handleToggle(checked)}
							disabled={isLoading}
						/>
					</div>

					<Separator />

					<form className='flex flex-col gap-4' onSubmit={handleSubmit}>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='push-balance-allowlist'>Allowlist package names</Label>
							<span className='text-xs text-muted-foreground'>
								Один Android package на строку, например com.example.bank
							</span>
							<Textarea
								id='push-balance-allowlist'
								value={allowlistText}
								onChange={e => setAllowlistText(e.target.value)}
								rows={6}
								disabled={isLoading}
								placeholder={'uz.kapitalbank\nuz.payme'}
								className='font-mono text-xs'
							/>
							<Button type='button' variant='outline' className='self-start' onClick={handleAddUzPreset} disabled={isLoading}>
								Добавить пресет UZ банков
							</Button>
						</div>

						<div className='flex flex-col gap-1.5 max-w-xs'>
							<Label htmlFor='push-balance-tg-min'>Мин. баланс для TG, UZS</Label>
							<span className='text-xs text-muted-foreground'>
								Для push-баланса. SMS по-прежнему используют фиксированный порог 700&nbsp;000 UZS.
							</span>
							<Input
								id='push-balance-tg-min'
								type='number'
								min={1}
								max={999_999_999}
								value={pushBalanceTgMinUzs}
								onChange={e => setPushBalanceTgMinUzs(Number(e.target.value))}
								disabled={isLoading}
							/>
						</div>

						<div className='rounded-md border border-dashed p-3 text-xs text-muted-foreground'>
							<p className='font-medium text-foreground mb-1'>Карты</p>
							<p>
								Push-баланс с разрешённого package использует тот же механизм синхронизации карт, что и SMS. В
								панели такой баланс помечается badge «Push».
							</p>
						</div>

						<Button type='submit' className='self-start' disabled={isLoading || isSaving}>
							Сохранить
						</Button>
					</form>
				</CardContent>
			</LoadingWrapper>
		</SettingsWidgetCard>
	);
}
